/**
 * imported-model bodyBindingId 工具函数。
 *
 * 文件职责：
 * 1. 统一区分离线包保存的稳定 bodyBindingId 与导入运行时 bodyBindingId；
 * 2. 为 metadata、topology repository 与 Mate 引用恢复提供同一套匹配规则；
 * 3. 避免各调用点用字符串拆分临时处理导致多实例装配体误命中。
 */

/** 运行时 bodyBindingId 为避免共享 mesh 冲突追加的分隔符。 */
const IMPORTED_RUNTIME_BODY_BINDING_ID_SEPARATOR = '::';

/**
 * 导入运行时为同源 body 的不同几何实例追加的作用域标记。
 *
 * 该标记只用于运行时 repository 内部隔离拓扑资产，不属于可交互 body 的持久化身份。
 */
const IMPORTED_RUNTIME_BODY_BINDING_GEOMETRY_SCOPE = `${IMPORTED_RUNTIME_BODY_BINDING_ID_SEPARATOR}geometry:`;

/**
 * 持久化 bodyBindingId 为区分装配 occurrence 追加的作用域标记。
 *
 * 该标记表达“同一个 STEP body 定义在某个组件实例中的可交互实体”，是离线包里的 transform authority。
 */
const IMPORTED_BODY_BINDING_OCCURRENCE_SCOPE = `${IMPORTED_RUNTIME_BODY_BINDING_ID_SEPARATOR}occurrence:`;

/** `.puppy` / sidecar 中 mesh 级 bodyBindingId 的前缀与末段包装格式。 */
const IMPORTED_MESH_BODY_BINDING_ID_PATTERN = /^mesh:[^:]+:body:(.+):[^:]+$/i;

/** Puppy CAD 内核 拓扑导出中 shell 与 body 子实体共享的稳定族标识格式。 */
const IMPORTED_TOPOLOGY_BODY_FAMILY_PATTERN = /^(.*)#(?:body|shell):([^:#/]+)$/i;

/**
 * 归一化 imported bodyBindingId。
 *
 * @param value 原始 bodyBindingId。
 * @returns 去除首尾空白后的 ID；非法值返回空字符串。
 */
export function normalizeImportedBodyBindingId(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * 判断 `::` 之后的片段是否是运行时追加的实例后缀。
 *
 * 设计说明：
 * 1. `geometryObjectId` 可能包含 `::zip-entry::`，因此不能把任意最后一段 `::` 都当成运行时后缀；
 * 2. 这里只识别当前导入/离线包链路明确会追加的 mesh 后缀，避免把资源路径误截断。
 *
 * @param suffix `::` 之后的候选后缀。
 * @returns 是否为运行时 mesh 后缀。
 */
function isRuntimeBodyBindingSuffix(suffix: string): boolean {
  return suffix.startsWith('mesh:')
    || suffix.includes('primitive-mesh')
    || suffix.startsWith('display-node-');
}

/**
 * 剥离一层已知运行时后缀。
 *
 * @param bodyBindingId 已归一化的 bodyBindingId。
 * @returns 剥离后的 bodyBindingId；无法识别时返回原值。
 */
function stripKnownRuntimeBodyBindingSuffix(bodyBindingId: string): string {
  const separatorIndex = bodyBindingId.lastIndexOf(IMPORTED_RUNTIME_BODY_BINDING_ID_SEPARATOR);
  if (separatorIndex <= 0) {
    return bodyBindingId;
  }

  const suffix = bodyBindingId.slice(separatorIndex + IMPORTED_RUNTIME_BODY_BINDING_ID_SEPARATOR.length);
  return isRuntimeBodyBindingSuffix(suffix)
    ? bodyBindingId.slice(0, separatorIndex)
    : bodyBindingId;
}

/**
 * 判断 bodyBindingId 是否携带运行时几何作用域。
 *
 * 设计说明：`::geometry:` 区分同源 body 在不同几何对象上的运行时拓扑资产。
 * 它不能被全局等价规则剥离，否则同源插入件会重新共享选择和拓扑目标。
 *
 * @param bodyBindingId 已归一化的 bodyBindingId。
 * @returns 是否携带运行时几何作用域。
 */
function hasRuntimeGeometryScope(bodyBindingId: string): boolean {
  return bodyBindingId.includes(IMPORTED_RUNTIME_BODY_BINDING_GEOMETRY_SCOPE);
}

/**
 * 判断 bodyBindingId 是否携带装配 occurrence 持久作用域。
 *
 * @param bodyBindingId 已归一化的 bodyBindingId。
 * @returns 是否携带装配 occurrence 作用域。
 */
function hasOccurrenceScope(bodyBindingId: string): boolean {
  return bodyBindingId.includes(IMPORTED_BODY_BINDING_OCCURRENCE_SCOPE);
}

/**
 * 剥离运行时或持久化作用域，恢复源 body 定义身份。
 *
 * @param bodyBindingId 已归一化的 bodyBindingId。
 * @returns 不含 geometry / occurrence scope 的源 bodyBindingId。
 */
function stripImportedBodyBindingScope(bodyBindingId: string): string {
  const scopeIndices = [
    bodyBindingId.indexOf(IMPORTED_RUNTIME_BODY_BINDING_GEOMETRY_SCOPE),
    bodyBindingId.indexOf(IMPORTED_BODY_BINDING_OCCURRENCE_SCOPE),
  ].filter((index) => index > 0);
  if (scopeIndices.length === 0) {
    return bodyBindingId;
  }

  return bodyBindingId.slice(0, Math.min(...scopeIndices));
}

/**
 * 解析 body 的源定义身份。
 *
 * @param bodyBindingId 持久化或运行时 bodyBindingId。
 * @returns 不含运行时 mesh 后缀、geometry scope 与 occurrence scope 的源 bodyBindingId。
 */
export function resolveImportedBodyDefinitionBindingId(bodyBindingId: string | null | undefined): string {
  return stripImportedBodyBindingScope(resolveStableImportedBodyBindingId(normalizeImportedBodyBindingId(bodyBindingId)));
}

/**
 * 创建装配 occurrence 级 bodyBindingId。
 *
 * 设计说明：同源 STEP body 可以共享 BRep/材质/拓扑资产定义，但不能共享可移动的交互身份。
 * 因此离线包持久化时必须把 body 定义身份提升为“body 定义 + 组件 occurrence”。
 *
 * @param bodyBindingId 源 bodyBindingId 或运行时 bodyBindingId。
 * @param occurrenceNodeId 当前 body 所属组件 occurrence 的 assemblyNodeId。
 * @returns 装配 occurrence 级 bodyBindingId；缺少 occurrence 时返回源定义身份。
 */
export function createImportedBodyOccurrenceBindingId(
  bodyBindingId: string | null | undefined,
  occurrenceNodeId: string | null | undefined,
): string {
  const bodyDefinitionId = resolveImportedBodyDefinitionBindingId(bodyBindingId);
  const normalizedOccurrenceNodeId = normalizeImportedBodyBindingId(occurrenceNodeId);
  if (!bodyDefinitionId || !normalizedOccurrenceNodeId) {
    return bodyDefinitionId;
  }

  return `${bodyDefinitionId}${IMPORTED_BODY_BINDING_OCCURRENCE_SCOPE}${encodeURIComponent(normalizedOccurrenceNodeId)}`;
}

/**
 * 从 occurrence 级 bodyBindingId 中解析组件实例节点 ID。
 *
 * 设计说明：
 * 离线包的 bodyBindingId 同时承担“源 body 定义”和“装配 occurrence”两层身份。Transform 解析需要的是后者，
 * 因此解析规则必须收口在 bodyBindingId 协议工具里，避免 Move、Mate、Selection 各自拆字符串。
 *
 * @param bodyBindingId 稳定或运行时 bodyBindingId。
 * @returns occurrence 对应的 assemblyNodeId；缺少 occurrence 作用域时返回空字符串。
 */
/**
 * 将已有 bodyBindingId 重绑定到新的组件 occurrence。
 *
 * 设计说明：
 * 项目模型挂载 `.puppy` 离线包时，包内 bodyBindingId 可能已经携带局部 occurrence；
 * 组合层必须只替换 occurrence 作用域，保留后续 `::geometry:` 等运行时几何隔离信息。
 * 这样 topology repository、selection metadata 与设计树实体条目会继续指向同一份几何资产，
 * 但 transform 写入目标会落到当前项目实例。
 *
 * @param bodyBindingId 原始 bodyBindingId。
 * @param occurrenceNodeId 新的项目组件 occurrence 节点 ID。
 * @returns 重绑定后的 bodyBindingId；缺少必要输入时返回归一化后的原始 ID。
 */
export function rebaseImportedBodyOccurrenceBindingId(
  bodyBindingId: string | null | undefined,
  occurrenceNodeId: string | null | undefined,
): string {
  const normalizedBodyBindingId = normalizeImportedBodyBindingId(bodyBindingId);
  const normalizedOccurrenceNodeId = normalizeImportedBodyBindingId(occurrenceNodeId);
  if (!normalizedBodyBindingId || !normalizedOccurrenceNodeId) {
    return normalizedBodyBindingId;
  }

  const encodedOccurrenceNodeId = encodeURIComponent(normalizedOccurrenceNodeId);
  const occurrenceScopeIndex = normalizedBodyBindingId.indexOf(IMPORTED_BODY_BINDING_OCCURRENCE_SCOPE);
  if (occurrenceScopeIndex >= 0) {
    const occurrenceValueStartIndex = occurrenceScopeIndex + IMPORTED_BODY_BINDING_OCCURRENCE_SCOPE.length;
    const nextScopeIndex = normalizedBodyBindingId.indexOf(
      IMPORTED_RUNTIME_BODY_BINDING_ID_SEPARATOR,
      occurrenceValueStartIndex,
    );
    const suffix = nextScopeIndex >= 0 ? normalizedBodyBindingId.slice(nextScopeIndex) : '';
    return `${normalizedBodyBindingId.slice(0, occurrenceScopeIndex)}${IMPORTED_BODY_BINDING_OCCURRENCE_SCOPE}${encodedOccurrenceNodeId}${suffix}`;
  }

  const geometryScopeIndex = normalizedBodyBindingId.indexOf(IMPORTED_RUNTIME_BODY_BINDING_GEOMETRY_SCOPE);
  if (geometryScopeIndex > 0) {
    return `${normalizedBodyBindingId.slice(0, geometryScopeIndex)}${IMPORTED_BODY_BINDING_OCCURRENCE_SCOPE}${encodedOccurrenceNodeId}${normalizedBodyBindingId.slice(geometryScopeIndex)}`;
  }

  return createImportedBodyOccurrenceBindingId(normalizedBodyBindingId, normalizedOccurrenceNodeId);
}

export function resolveImportedBodyOccurrenceNodeId(bodyBindingId: string | null | undefined): string {
  const normalizedBodyBindingId = normalizeImportedBodyBindingId(bodyBindingId);
  const scopeIndex = normalizedBodyBindingId.indexOf(IMPORTED_BODY_BINDING_OCCURRENCE_SCOPE);
  if (scopeIndex < 0) {
    return '';
  }

  const occurrenceStartIndex = scopeIndex + IMPORTED_BODY_BINDING_OCCURRENCE_SCOPE.length;
  const nextScopeIndex = normalizedBodyBindingId.indexOf(
    IMPORTED_RUNTIME_BODY_BINDING_ID_SEPARATOR,
    occurrenceStartIndex,
  );
  const encodedOccurrenceNodeId = (
    nextScopeIndex >= 0
      ? normalizedBodyBindingId.slice(occurrenceStartIndex, nextScopeIndex)
      : normalizedBodyBindingId.slice(occurrenceStartIndex)
  ).trim();
  if (!encodedOccurrenceNodeId) {
    return '';
  }

  try {
    return decodeURIComponent(encodedOccurrenceNodeId).trim();
  } catch {
    return encodedOccurrenceNodeId;
  }
}

/**
 * 从运行时 bodyBindingId 还原保存时的稳定 bodyBindingId。
 *
 * @param bodyBindingId 稳定或运行时 bodyBindingId。
 * @returns 稳定 bodyBindingId。
 */
export function resolveStableImportedBodyBindingId(bodyBindingId: string): string {
  let currentBodyBindingId = normalizeImportedBodyBindingId(bodyBindingId);
  while (currentBodyBindingId) {
    const nextBodyBindingId = stripKnownRuntimeBodyBindingSuffix(currentBodyBindingId);
    if (nextBodyBindingId === currentBodyBindingId) {
      return currentBodyBindingId;
    }
    currentBodyBindingId = nextBodyBindingId;
  }
  return currentBodyBindingId;
}

/**
 * 向数组追加非空且未重复的 key。
 *
 * @param target 目标 key 数组。
 * @param value 候选 key。
 */
function appendUniqueKey(target: string[], value: string | null | undefined): void {
  const normalizedValue = normalizeImportedBodyBindingId(value);
  if (normalizedValue && !target.includes(normalizedValue)) {
    target.push(normalizedValue);
  }
}

/**
 * 从 mesh 级 bodyBindingId 中还原原始 bodyId。
 *
 * @param bodyBindingId 稳定或运行时 bodyBindingId。
 * @returns 原始 bodyId；非 mesh 包装格式返回空字符串。
 */
function extractBodyIdFromMeshBodyBindingId(bodyBindingId: string): string {
  const stableBodyBindingId = resolveImportedBodyDefinitionBindingId(bodyBindingId);
  const match = IMPORTED_MESH_BODY_BINDING_ID_PATTERN.exec(stableBodyBindingId);
  return match?.[1] ?? '';
}

/**
 * 从 imported bodyBindingId 或已污染的 bodyId 中解析稳定 body 定义 ID。
 *
 * 设计说明：
 * `bodyBindingId` 可以携带 mesh 包装、geometry scope 与 occurrence scope；这些字段属于交互绑定身份。
 * `bodyId` 只表达零件内稳定 body 定义，因此必须剥离绑定包装和运行时作用域，避免设计树与元数据继续传播技术 ID。
 *
 * @param value bodyBindingId、运行时 bodyBindingId 或已污染的 bodyId。
 * @returns 稳定 body 定义 ID；无法识别 mesh 包装时返回剥离作用域后的稳定绑定 ID。
 */
export function resolveImportedBodyDefinitionId(value: string | null | undefined): string {
  const normalizedValue = normalizeImportedBodyBindingId(value);
  if (!normalizedValue) {
    return '';
  }

  return extractBodyIdFromMeshBodyBindingId(normalizedValue)
    || resolveStableImportedBodyBindingId(normalizedValue);
}

/**
 * 将 Puppy CAD 内核 shell/body 子实体 ID 归一到同一个 body 族 key。
 *
 * 设计说明：
 * 1. 正常装配 hierarchy 常记录 `#shell:n`，离线包 BRep/display 绑定常记录 `#body:n`；
 * 2. 二者描述的是同一个零件内的同源拓扑 body，导出归属和导入选择都必须能互相匹配；
 * 3. 这里只归一化明确的 `#shell` / `#body` 后缀，不模糊匹配其它子实体，避免跨 body 误命中。
 *
 * @param value 原始拓扑 body 或 shell ID。
 * @returns body 族 key；无法识别时返回空字符串。
 */
export function resolveImportedTopologyBodyFamilyKey(value: string | null | undefined): string {
  const normalizedValue = normalizeImportedBodyBindingId(value);
  if (!normalizedValue) {
    return '';
  }

  const match = IMPORTED_TOPOLOGY_BODY_FAMILY_PATTERN.exec(normalizedValue);
  return match ? `${match[1]}#topology-body:${match[2]}` : '';
}

/**
 * 读取源拓扑协议的一基 Body/Shell 序号，转换为内核选择器的零基序号。
 * 仅接受稳定拓扑 ID，不从显示名称、场景顺序或 geometryObjectId 猜测编号。
 * @param value 源拓扑 Body ID。
 * @returns 明确的源序号；非编号型协议 ID 返回 undefined。
 */
export function resolveImportedTopologyBodyOrdinal(value: string | null | undefined): number | undefined {
  const match = IMPORTED_TOPOLOGY_BODY_FAMILY_PATTERN.exec(normalizeImportedBodyBindingId(value));
  if (!match || !/^[1-9]\d*$/.test(match[2]!)) return undefined;
  const ordinal = Number(match[2]) - 1;
  return Number.isSafeInteger(ordinal) ? ordinal : undefined;
}

/**
 * 为一次 bodyBindingId 查询生成稳定/运行时候选 key。
 *
 * @param bodyBindingId 稳定或运行时 bodyBindingId。
 * @returns 去重后的查询 key；第一个永远是调用方传入的精确 ID。
 */
export function createImportedBodyBindingLookupKeys(bodyBindingId: string | null | undefined): readonly string[] {
  const normalizedBodyBindingId = normalizeImportedBodyBindingId(bodyBindingId);
  if (!normalizedBodyBindingId) {
    return [];
  }

  const stableBodyBindingId = resolveStableImportedBodyBindingId(normalizedBodyBindingId);
  return stableBodyBindingId === normalizedBodyBindingId
    ? [normalizedBodyBindingId]
    : [normalizedBodyBindingId, stableBodyBindingId];
}

/**
 * 为 body 归属匹配生成候选 key。
 *
 * 设计说明：
 * 1. `bodyBindingId` 需要兼容运行时后缀、mesh 包装和 Puppy CAD 内核 shell/body 差异；
 * 2. 调用方仍然先按精确 key 命中，只有精确命中失败时才会走派生 key；
 * 3. 携带 `::geometry:` 的运行时 ID 代表已被隔离的拓扑资产，不能回退到同源 bodyId。
 *
 * @param bodyBindingId 稳定或运行时 bodyBindingId / bodyId。
 * @returns 去重后的归属匹配 key。
 */
export function createImportedBodyOwnershipLookupKeys(
  bodyBindingId: string | null | undefined,
): readonly string[] {
  const keys: string[] = [];
  const normalizedBodyBindingId = normalizeImportedBodyBindingId(bodyBindingId);
  const hasInstanceScope = hasRuntimeGeometryScope(normalizedBodyBindingId) || hasOccurrenceScope(normalizedBodyBindingId);
  createImportedBodyBindingLookupKeys(normalizedBodyBindingId).forEach((key) => {
    appendUniqueKey(keys, key);
    appendUniqueKey(keys, resolveImportedTopologyBodyFamilyKey(key));
  });

  if (!hasInstanceScope) {
    const extractedBodyId = extractBodyIdFromMeshBodyBindingId(normalizedBodyBindingId);
    appendUniqueKey(keys, extractedBodyId);
    appendUniqueKey(keys, resolveImportedTopologyBodyFamilyKey(extractedBodyId));
  }
  return keys;
}

/**
 * 判断两个 bodyBindingId 是否指向同一个稳定 body。
 *
 * @param left 左侧 bodyBindingId。
 * @param right 右侧 bodyBindingId。
 * @returns 是否稳定语义一致。
 */
export function areImportedBodyBindingIdsEquivalent(
  left: string | null | undefined,
  right: string | null | undefined,
): boolean {
  const normalizedLeft = normalizeImportedBodyBindingId(left);
  const normalizedRight = normalizeImportedBodyBindingId(right);
  if (!normalizedLeft || !normalizedRight) {
    return false;
  }

  const rightLookupKeys = new Set(createImportedBodyOwnershipLookupKeys(normalizedRight));
  return createImportedBodyOwnershipLookupKeys(normalizedLeft).some((lookupKey) => rightLookupKeys.has(lookupKey));
}
